import { Component, OnInit, inject, signal } from '@angular/core';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { EnterpriseContractRecord, SalesApiService } from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-enterprise-contracts',
  imports: [AinAmountPipe, AinDatePipe, AinPageComponent, AinStateComponent, TableModule, ButtonModule],
  template: `
    <ain-page [title]="t('sales.contracts.title')" [description]="t('sales.contracts.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="15" [selection]="selectedRows()" (selectionChange)="onSelectionChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width:44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.contracts.fields.contractNumber') }}</th>
                <th>{{ t('sales.contracts.fields.totalValue') }}</th>
                <th>{{ t('sales.contracts.fields.effectiveFrom') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.contractNumber }}</button></td>
                <td>{{ row.totalContractValue | ainAmount }} {{ row.currencyCode }}</td>
                <td>{{ row.effectiveFromUtc | ainDate }}</td>
                <td>{{ row.status }}</td>
              </tr>
            </ng-template>
          </p-table>
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`.panel{display:grid;gap:10px}.toolbar{display:flex;gap:8px;flex-wrap:wrap}.link-btn{border:0;background:none;color:#1d4ed8;cursor:pointer;font-weight:600;padding:0}.error{color:#b91c1c}`]
})
export class SalesEnterpriseContractsComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly error = signal('');
  readonly rows = signal<EnterpriseContractRecord[]>([]);
  readonly selectedRows = signal<EnterpriseContractRecord[]>([]);

  ngOnInit(): void { this.load(); }
  load(): void {
    this.loading.set(true);
    this.api.listEnterpriseContracts().subscribe({
      next: items => { this.rows.set(items); this.loading.set(false); },
      error: () => { this.rows.set([]); this.error.set(this.t('sales.contracts.errorLoad')); this.loading.set(false); }
    });
  }
  onSelectionChange(s: EnterpriseContractRecord[] | EnterpriseContractRecord | null): void {
    this.selectedRows.set(Array.isArray(s) ? s : s ? [s] : []);
  }
  openCreate(): void { void this.router.navigate(['/sales/contracts/new']); }
  openSelected(): void { const r = this.selectedRows()[0]; if (r) this.openDetail(r.id); }
  openDetail(id: string): void { void this.router.navigate(['/sales/contracts', id]); }
  t(key: string): string { return this.localization.translate(key); }
}
